/**
 * Calendar-day arithmetic on ISO dates ("YYYY-MM-DD").
 *
 * Days are plain strings, never `Date` objects, so there is no time zone in the
 * domain at all: "12 Oct" is the same day on the server, in the browser and in
 * a test. Only the edge (the Worker) decides what "today" is, in the kitchen's
 * time zone.
 */

export type IsoDate = string;

const MS_PER_DAY = 86_400_000;

export function isIsoDate(value: string): value is IsoDate {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const t = Date.parse(`${value}T00:00:00Z`);
  return Number.isFinite(t) && new Date(t).toISOString().slice(0, 10) === value;
}

function toUtc(d: IsoDate): number {
  const t = Date.parse(`${d}T00:00:00Z`);
  if (!Number.isFinite(t)) throw new RangeError(`Invalid date: ${d}`);
  return t;
}

function fromUtc(t: number): IsoDate {
  return new Date(t).toISOString().slice(0, 10);
}

export function addDays(d: IsoDate, n: number): IsoDate {
  return fromUtc(toUtc(d) + n * MS_PER_DAY);
}

/** Whole days from `a` to `b` (positive when b is later). */
export function diffDays(a: IsoDate, b: IsoDate): number {
  return Math.round((toUtc(b) - toUtc(a)) / MS_PER_DAY);
}

/** ISO weekday: 1 = Monday ... 7 = Sunday. */
export function isoWeekday(d: IsoDate): number {
  const js = new Date(toUtc(d)).getUTCDay(); // 0 = Sunday
  return js === 0 ? 7 : js;
}

export function compareDates(a: IsoDate, b: IsoDate): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

export function eachDay(from: IsoDate, to: IsoDate): IsoDate[] {
  const out: IsoDate[] = [];
  for (let d = from; d <= to; d = addDays(d, 1)) out.push(d);
  return out;
}

/** "YYYY-MM" of a date. */
export function monthOf(d: IsoDate): string {
  return d.slice(0, 7);
}

export function monthStart(month: string): IsoDate {
  return `${month}-01`;
}

export function monthEnd(month: string): IsoDate {
  const [y, m] = month.split('-').map(Number) as [number, number];
  return fromUtc(Date.UTC(y, m, 0));
}

export function addMonths(month: string, n: number): string {
  const [y, m] = month.split('-').map(Number) as [number, number];
  const t = new Date(Date.UTC(y, m - 1 + n, 1));
  return t.toISOString().slice(0, 7);
}

const DAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTH_LONG = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/** "Tue 20 Oct" */
export function formatDay(d: IsoDate): string {
  const t = new Date(toUtc(d));
  return `${DAY_SHORT[t.getUTCDay()]} ${t.getUTCDate()} ${MONTH_SHORT[t.getUTCMonth()]}`;
}

/** "20 Oct" */
export function formatShort(d: IsoDate): string {
  const t = new Date(toUtc(d));
  return `${t.getUTCDate()} ${MONTH_SHORT[t.getUTCMonth()]}`;
}

/** "20 Oct 2026" */
export function formatFull(d: IsoDate): string {
  const t = new Date(toUtc(d));
  return `${t.getUTCDate()} ${MONTH_SHORT[t.getUTCMonth()]} ${t.getUTCFullYear()}`;
}

/** "20/10/2026" */
export function formatIndianDate(d: IsoDate): string {
  const [y, m, day] = d.split('-');
  return `${day}/${m}/${y}`;
}

/** "October 2026" */
export function formatMonth(month: string): string {
  const [y, m] = month.split('-').map(Number) as [number, number];
  return `${MONTH_LONG[m - 1]} ${y}`;
}

/** Compact range: "13-14 Oct", "30 Oct - 2 Nov". Expects a sorted list of days. */
export function formatRanges(days: IsoDate[]): string {
  if (days.length === 0) return '';
  const sorted = [...days].sort();
  const runs: Array<[IsoDate, IsoDate]> = [];
  for (const d of sorted) {
    const last = runs[runs.length - 1];
    if (last && diffDays(last[1], d) === 1) last[1] = d;
    else runs.push([d, d]);
  }
  return runs
    .map(([a, b]) => {
      if (a === b) return formatShort(a);
      const ta = new Date(toUtc(a));
      const tb = new Date(toUtc(b));
      return ta.getUTCMonth() === tb.getUTCMonth()
        ? `${ta.getUTCDate()}-${tb.getUTCDate()} ${MONTH_SHORT[tb.getUTCMonth()]}`
        : `${formatShort(a)} - ${formatShort(b)}`;
    })
    .join(', ');
}

/** Today's date in a given IANA time zone (default: India). */
export function todayIn(timeZone = 'Asia/Kolkata', now: Date = new Date()): IsoDate {
  // en-CA formats as YYYY-MM-DD
  return new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now);
}
